"use client"

import { useGSAP } from "@gsap/react"
import gsap from "gsap"

gsap.registerPlugin(useGSAP)

/**
 * Item customize-page entrance choreography (mirrors
 * `../menu-animations.tsx` / `components/home/home-animations.tsx`). Renders
 * nothing — mounted only by the FULL page (`./page.tsx`), never the
 * intercepting-route modal, it animates the server-rendered body via
 * `data-item-*` hooks: the photo settles from a subtle zoom, the header block
 * rises in, then the customizer body fades up beneath it.
 *
 * Only plain server-rendered elements are animated (the `<header>`, the `<img>`,
 * and the `[data-item-body]` WRAPPER div — never the client customizer inside
 * it, which hydration can re-render mid-tween and strand at the from-state).
 * Everything sits inside `gsap.matchMedia("(prefers-reduced-motion:
 * no-preference)")`, and setup is deferred two frames so tweens bind to the
 * settled DOM.
 */
export function ItemAnimations() {
  useGSAP((_context, contextSafe) => {
    const has = (selector: string) => document.querySelector(selector) !== null

    const setup = contextSafe!(() => {
      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // fromTo (NOT from): `from` records the element's CURRENT opacity as the
        // tween END, so a soft-nav re-run firing mid-fade captures a partial
        // value as the target and strands it there. fromTo pins an explicit
        // visible end-state → every re-run is idempotent.
        const tl = gsap.timeline({ defaults: { ease: "power2.out" } })

        if (has("[data-item-media]")) {
          tl.fromTo(
            "[data-item-media]",
            { autoAlpha: 0, scale: 1.05 },
            { autoAlpha: 1, scale: 1, duration: 0.55 }
          )
        }
        if (has("[data-item-header]")) {
          tl.fromTo(
            "[data-item-header]",
            { autoAlpha: 0, y: 16 },
            { autoAlpha: 1, y: 0, duration: 0.45 },
            "-=0.3"
          )
        }
        if (has("[data-item-body]")) {
          tl.fromTo(
            "[data-item-body]",
            { autoAlpha: 0, y: 20 },
            { autoAlpha: 1, y: 0, duration: 0.5 },
            "-=0.2"
          )
        }
      })
    })

    const raf = requestAnimationFrame(() => requestAnimationFrame(setup))
    return () => cancelAnimationFrame(raf)
  })

  return null
}
